import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calculator } from "lucide-react";
import { fadeUp, stagger } from "@/data/landingData";
import { usePricing } from "@/lib/pricing";

// Calculator-friendly subset (only m² + bm priced services).
const CALC_SLUGS = ["holodomy", "zateplenie", "zaklady", "dlazba", "sadrokarton"];

export default function ROICalculator() {
  const { items } = usePricing();
  const calcItems = items.filter((i) => CALC_SLUGS.includes(i.slug));
  const [size, setSize] = useState(150);
  const [slug, setSlug] = useState("holodomy");

  // If the previously-selected slug disappears (admin changed pricing),
  // fall back to the first available item.
  useEffect(() => {
    if (calcItems.length > 0 && !calcItems.find((i) => i.slug === slug)) {
      setSlug(calcItems[0].slug);
    }
  }, [calcItems, slug]);

  const active = calcItems.find((i) => i.slug === slug) || calcItems[0];
  const total = active ? size * active.rate : 0;
  const savings = Math.round(total * 0.12);

  return (
    <section className="py-24 lg:py-32 section-dark" data-testid="calculator">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.div
            variants={fadeUp}
            className="inline-flex items-center gap-2 bg-[#00E5FF]/10 text-[#00E5FF] px-4 py-2 rounded-full text-sm mb-4"
          >
            <Calculator size={16} />
            <span className="mono uppercase tracking-wider text-xs">Kalkulačka</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-4xl sm:text-5xl font-black tracking-tight"
          >
            Spočítajte si <span className="text-[#FF5E00]">cenu</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-8 tech-grid"
        >
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <label className="mono text-xs text-zinc-500 uppercase tracking-wider mb-3 block">
                  Typ projektu
                </label>
                <div className="flex flex-wrap gap-2">
                  {calcItems.map((it) => (
                    <button
                      key={it.slug}
                      onClick={() => setSlug(it.slug)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        slug === it.slug
                          ? "bg-[#00E5FF] text-black"
                          : "bg-white/10 text-zinc-400 hover:bg-white/20"
                      }`}
                      data-testid={`calc-${it.slug}`}
                    >
                      {it.title}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mono text-xs text-zinc-500 uppercase tracking-wider mb-3 block">
                  Rozloha: <span className="text-white">{size} m²</span>
                </label>
                <input
                  type="range"
                  min="50"
                  max="500"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                  className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer accent-[#00E5FF]"
                  data-testid="calc-slider"
                />
              </div>
            </div>

            <div className="bg-black/40 rounded-2xl p-6 border border-white/10">
              <p className="mono text-xs text-zinc-500 uppercase tracking-wider mb-2">
                Orientačná cena
              </p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-black text-[#00E5FF]">
                  {total.toLocaleString()}
                </span>
                <span className="text-2xl text-zinc-500">€</span>
              </div>

              <div className="space-y-2 text-sm mb-6">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Cena za {(active?.unit || "m²").replace(/^€\//, "")}</span>
                  <span className="mono text-white">{active?.rate || 0} €</span>
                </div>
                <div className="flex justify-between text-green-400">
                  <span>Vaša úspora</span>
                  <span className="font-bold">~{savings.toLocaleString()} €</span>
                </div>
              </div>

              <a href="#kontakt" className="btn-primary w-full text-center block" data-testid="calc-cta">
                Získať presnú ponuku
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
